/**
 * Tabella aggregata con drill-down a due livelli: click su una riga del
 * raggruppamento principale espande un livello intermedio aggregato per
 * mansione (stessa formula di `domain/costoPersonaleReport.js::
 * aggregaCostoPersonale`, ricalcolata qui lato client sulle righe già
 * scaricate dall'API — nessuna chiamata aggiuntiva); click su una riga
 * mansione espande infine l'elenco piatto delle singole righe di costo
 * (persona/periodo/categoria/ore/costo) che la compongono.
 *
 * Quando il raggruppamento principale è già "Mansione" (groupBy ===
 * "mansioneCodice") il livello intermedio sarebbe tautologico (un solo
 * valore, se stesso) — si salta dritti alle righe piatte, come prima di
 * questa modifica. Per "Gruppo mansioni" invece ha senso (un gruppo
 * contiene più mansioni), quindi lì il livello intermedio resta.
 */
import React, { useEffect, useState } from "react";
import { euro, ore, labelChiave } from "./format";

// Entità presenti nel costo personale ma che non sono RSA (nessun obbligo
// normativo, nessun ospite, fuori dalla logica fabbisogno/copertura) — per
// queste il "capacità non configurata" sarebbe un avviso fuorviante: non è
// un dato mancante, è strutturalmente non applicabile. Decisione con
// Matteo, 2026-09-16: si registrano solo le spese di personale, niente
// configurazione RSA in phc_hrstdrsaconfigs per non farle entrare nel
// motore di fabbisogno.
const RSA_NON_RESIDENZIALI = ["Head Quarter"];

function dettaglioDiGruppo(righe, groupBy, chiave, aggregato) {
  if (groupBy === "periodo") {
    return righe.filter(
      (r) => `${r.anno}-${String(r.mese).padStart(2, "0")}` === chiave,
    );
  }
  if (groupBy === "gruppoCosto") {
    // Un gruppo custom non è una colonna diretta sulla riga (una mansione
    // può stare in più gruppi) — filtra sulla lista mansioni del gruppo,
    // già restituita dal backend su ogni aggregato.
    const mansioni = aggregato?.mansioni || [];
    return righe.filter(
      (r) => r.mansioneCodice && mansioni.includes(r.mansioneCodice),
    );
  }
  return righe.filter((r) => (r[groupBy] ?? "non_classificato") === chiave);
}

function round2(v) {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

// Segnaposto visivo armonizzato/ordinario (domain/armonizzazione.js) nel
// dettaglio riga-per-riga — un dipendente è "armonizzato" se assunto PRIMA
// dell'avvio della sua RSA in Peralba, "ordinario" altrimenti. null quando
// la data di assunzione non è nota (mai un valore inventato).
function BadgeArmonizzazione({ stato }) {
  if (stato === "armonizzato") {
    return (
      <span
        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700"
        title="Armonizzato: assunto prima dell'avvio della RSA in Peralba"
      >
        ARM
      </span>
    );
  }
  if (stato === "ordinario") {
    return (
      <span
        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600"
        title="Ordinario: assunto dall'avvio della RSA in Peralba in poi"
      >
        ORD
      </span>
    );
  }
  return (
    <span
      className="text-gray-300 text-[10px]"
      title="Armonizzazione non determinabile: data di assunzione non disponibile"
    >
      —
    </span>
  );
}

/**
 * Aggrega per mansione le righe già filtrate di un gruppo principale
 * (livello intermedio del drill-down). Stessa formula di
 * `aggregaCostoPersonale` nel domain backend (somma costo/ore, media
 * oraria = costo totale / ore totali) ma calcolata qui sulle righe che il
 * client ha già in memoria — evita una seconda chiamata API per un
 * sottoinsieme di dati già scaricato. Etichetta con il nome esteso della
 * mansione dal catalogo (`mansioniCatalogo`, da `GET /costo-personale/filtri`),
 * mai il codice grezzo.
 */
function aggregaPerMansione(righe, mansioniCatalogo) {
  const nomeDiCodice = new Map(
    (mansioniCatalogo || []).map((m) => [m.codice, m.nome]),
  );
  const groups = new Map();
  for (const r of righe) {
    const codice = r.mansioneCodice ?? "non_classificato";
    if (!groups.has(codice)) {
      groups.set(codice, {
        chiave: codice,
        costoTotale: 0,
        oreTotali: 0,
        righe: 0,
      });
    }
    const g = groups.get(codice);
    g.costoTotale = round2(g.costoTotale + r.costoTotale);
    g.oreTotali = round2(g.oreTotali + r.oreTotali);
    g.righe += 1;
  }
  return [...groups.values()]
    .map((g) => ({
      ...g,
      nome:
        g.chiave === "non_classificato"
          ? "Non classificato"
          : nomeDiCodice.get(g.chiave) || g.chiave,
      costoMedioOrario:
        g.oreTotali > 0 ? round2(g.costoTotale / g.oreTotali) : null,
    }))
    .sort((a, b) => b.costoTotale - a.costoTotale);
}

/**
 * Livello intermedio del drill-down: righe di un gruppo principale
 * aggregate per mansione. Nessun cap (molte meno righe del livello più
 * interno — una per mansione distinta). Click su una riga espande
 * `RigheDettaglio` (con il suo cap) filtrato a quella mansione.
 */
function RaggruppamentoMansioni({
  righe,
  mansioniCatalogo,
  espansaMansione,
  onToggleMansione,
}) {
  const aggregati = aggregaPerMansione(righe, mansioniCatalogo);
  if (!aggregati.length) {
    return <p className="text-xs text-gray-400 py-2 px-3">Nessuna riga</p>;
  }
  return (
    <table className="min-w-full text-xs">
      <thead>
        <tr className="text-left text-gray-400 uppercase">
          <th className="py-1 px-3">Mansione</th>
          <th className="py-1 px-3 text-right">Righe</th>
          <th className="py-1 px-3 text-right">Ore</th>
          <th className="py-1 px-3 text-right">€/ora</th>
          <th className="py-1 px-3 text-right">Costo totale</th>
        </tr>
      </thead>
      <tbody>
        {aggregati.map((m) => {
          const aperta = espansaMansione === m.chiave;
          return (
            <React.Fragment key={m.chiave}>
              <tr
                className="border-t border-gray-100 hover:bg-gray-100 cursor-pointer"
                onClick={() => onToggleMansione(m.chiave)}
              >
                <td className="py-1 px-3">
                  <span className="text-gray-400 mr-1">
                    {aperta ? "▾" : "▸"}
                  </span>
                  {m.nome}
                </td>
                <td className="py-1 px-3 text-right tabular-nums">{m.righe}</td>
                <td className="py-1 px-3 text-right tabular-nums">
                  {ore(m.oreTotali)}
                </td>
                <td className="py-1 px-3 text-right tabular-nums">
                  {m.costoMedioOrario != null ? euro(m.costoMedioOrario) : "—"}
                </td>
                <td className="py-1 px-3 text-right tabular-nums">
                  {euro(m.costoTotale)}
                </td>
              </tr>
              {aperta && (
                <tr className="bg-white">
                  <td colSpan={5} className="p-0">
                    <RigheDettaglio
                      righe={righe.filter(
                        (r) =>
                          (r.mansioneCodice ?? "non_classificato") === m.chiave,
                      )}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// Un gruppo può contenere migliaia di righe (es. tutta un'RSA su più mesi)
// — senza limite il drill-down renderizza migliaia di <tr> e la pagina
// esplode in altezza (osservato: ~95000px su un gruppo da 3759 righe).
// Mostra le prime N in uno scroll interno, con nota se troncato.
const MAX_RIGHE_DETTAGLIO = 200;

function RigheDettaglio({ righe }) {
  if (!righe.length) {
    return <p className="text-xs text-gray-400 py-2 px-3">Nessuna riga</p>;
  }
  const troncato = righe.length > MAX_RIGHE_DETTAGLIO;
  const daMostrare = troncato ? righe.slice(0, MAX_RIGHE_DETTAGLIO) : righe;
  return (
    <div>
      <div className="max-h-80 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400 uppercase sticky top-0 bg-gray-50">
              <th className="py-1 px-3">Persona</th>
              <th className="py-1 px-3">Stato</th>
              <th className="py-1 px-3">RSA</th>
              <th className="py-1 px-3">Mansione</th>
              <th className="py-1 px-3">Periodo</th>
              <th className="py-1 px-3">Categoria</th>
              <th className="py-1 px-3 text-right">Ore</th>
              <th className="py-1 px-3 text-right">Costo</th>
            </tr>
          </thead>
          <tbody>
            {daMostrare.map((r, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1 px-3">
                  {r.cognome} {r.nome}
                </td>
                <td className="py-1 px-3">
                  <BadgeArmonizzazione stato={r.armonizzazione} />
                </td>
                <td className="py-1 px-3">{r.rsa || "—"}</td>
                <td className="py-1 px-3">{r.mansioneCodice || "—"}</td>
                <td className="py-1 px-3">
                  {r.anno}-{String(r.mese).padStart(2, "0")}
                </td>
                <td className="py-1 px-3">{r.descrizioneIndirizzamento}</td>
                <td className="py-1 px-3 text-right tabular-nums">
                  {ore(r.oreTotali)}
                </td>
                <td className="py-1 px-3 text-right tabular-nums">
                  {euro(r.costoTotale)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {troncato && (
        <p className="text-xs text-gray-400 py-1 px-3">
          Mostrate le prime {MAX_RIGHE_DETTAGLIO} di {righe.length} righe —
          affina i filtri (periodo/persona) per il dettaglio completo.
        </p>
      )}
    </div>
  );
}

export default function AggregatoTable({
  aggregati,
  righe,
  groupBy,
  groupByLabel,
  mansioni,
}) {
  const [espansa, setEspansa] = useState(null);
  const [espansaMansione, setEspansaMansione] = useState(null);
  // Cambiare il raggruppamento principale invalida entrambi i livelli di
  // espansione (le chiavi non sono comparabili tra un groupBy e l'altro,
  // e una coincidenza di codice lascerebbe un livello interno aperto su
  // dati non più coerenti).
  useEffect(() => {
    setEspansa(null);
    setEspansaMansione(null);
  }, [groupBy]);
  // Costo/ospite: il backend lo calcola solo per groupBy=rsa (capacità è
  // per-RSA) — vedi services/costoPersonaleService.js. Colonne extra solo
  // in quel caso, non per altri raggruppamenti dove non avrebbe senso.
  const mostraCostoOspite = groupBy === "rsa";
  // Vedi commento in cima al file: per groupBy="mansioneCodice" il livello
  // intermedio "per mansione" sarebbe un gruppo unico uguale a se stesso.
  const saltaLivelloIntermedio = groupBy === "mansioneCodice";

  function toggleGruppoPrincipale(chiave) {
    const chiudi = espansa === chiave;
    setEspansa(chiudi ? null : chiave);
    setEspansaMansione(null);
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            <th className="py-2 px-3">{groupByLabel}</th>
            <th className="py-2 px-3 text-right">Righe</th>
            <th className="py-2 px-3 text-right">Ore</th>
            <th className="py-2 px-3 text-right">€/ora</th>
            <th className="py-2 px-3 text-right">Costo totale</th>
            {mostraCostoOspite && (
              <>
                <th className="py-2 px-3 text-right">Posti</th>
                <th className="py-2 px-3 text-right">€/ospite/giorno</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {aggregati.map((g) => {
            const aperta = espansa === g.chiave;
            return (
              <React.Fragment key={g.chiave}>
                <tr
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleGruppoPrincipale(g.chiave)}
                >
                  <td className="py-2 px-3">
                    <span className="text-gray-400 mr-1">
                      {aperta ? "▾" : "▸"}
                    </span>
                    {labelChiave(g, groupBy)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {g.righe}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {ore(g.oreTotali)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {g.costoMedioOrario != null
                      ? euro(g.costoMedioOrario)
                      : "—"}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {euro(g.costoTotale)}
                  </td>
                  {mostraCostoOspite &&
                    (RSA_NON_RESIDENZIALI.includes(g.chiave) ? (
                      <td
                        colSpan={2}
                        className="py-2 px-3 text-right text-gray-400 text-xs"
                        title="Non è una RSA residenziale (sede amministrativa/centrale) — nessun ospite, costo per ospite non applicabile"
                      >
                        — non residenziale
                      </td>
                    ) : (
                      <>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {g.capacita ?? (
                            <span
                              className="text-yellow-600"
                              title="Capacità non configurata per questa RSA in Normative → Configurazione RSA (o nome RSA non corrispondente tra CSV e anagrafica)"
                            >
                              ⚠ n/d
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {g.costoGiornalieroPerOspite != null
                            ? euro(g.costoGiornalieroPerOspite)
                            : "—"}
                        </td>
                      </>
                    ))}
                </tr>
                {aperta && (
                  <tr className="bg-gray-50">
                    <td colSpan={mostraCostoOspite ? 7 : 5} className="p-0">
                      {saltaLivelloIntermedio ? (
                        <RigheDettaglio
                          righe={dettaglioDiGruppo(righe, groupBy, g.chiave, g)}
                        />
                      ) : (
                        <RaggruppamentoMansioni
                          righe={dettaglioDiGruppo(righe, groupBy, g.chiave, g)}
                          mansioniCatalogo={mansioni}
                          espansaMansione={espansaMansione}
                          onToggleMansione={(codice) =>
                            setEspansaMansione(
                              espansaMansione === codice ? null : codice,
                            )
                          }
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {aggregati.length === 0 && (
            <tr>
              <td
                colSpan={mostraCostoOspite ? 7 : 5}
                className="py-6 text-center text-gray-400"
              >
                Nessun dato
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
